import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import apiUrls from "../../../api";
import type { RootState } from "../../../common/state/store";
import type { HelpArticle } from "../components/HelpArticleSection/useHelpArticle";

export type HelpArticleStatus = "loading" | "ready" | "none";

interface HelpArticleEntry {
  status: HelpArticleStatus;
  article: HelpArticle | null;
}

interface HelpArticlesState {
  // Keyed by `${lang}::${modelName}` so an org that switches UI language keeps
  // both variants cached instead of clobbering. Never cleared on graph reset —
  // articles are per content-type, shared across projects.
  byKey: Record<string, HelpArticleEntry>;
}

const initialState: HelpArticlesState = { byKey: {} };

export const helpArticleKey = (lang: string, modelName: string) => `${lang}::${modelName}`;

/**
 * Fetch (and cache) the mapped HelpDocs article for one content type. Prefetched
 * in bulk when a graph loads (ProjectScenes) so the node-form (?) button and its
 * panel are warm by the time the user opens a node; also dispatched lazily by
 * useHelpArticle as a self-heal for anything the prefetch missed. The `condition`
 * guard makes both paths idempotent: a key that's already loading/ready/none is
 * never refetched within the session.
 */
export const fetchHelpArticle = createAsyncThunk<
  { key: string; article: HelpArticle | null },
  { modelName: string; lang: string },
  { state: RootState }
>(
  "helpArticles/fetch",
  async ({ modelName, lang }) => {
    const response = await axios.get<{ article: HelpArticle | null }>(
      `${apiUrls.helpArticles}/${modelName}?lang=${lang}`,
    );
    return { key: helpArticleKey(lang, modelName), article: response.data?.article ?? null };
  },
  {
    condition: ({ modelName, lang }, { getState }) => {
      if (!modelName) return false;
      // Skip if we already have (or are fetching) this key — dedupes the bulk
      // prefetch against the lazy per-node dispatch and StrictMode double-invoke.
      return !getState().helpArticles.byKey[helpArticleKey(lang, modelName)];
    },
  },
);

const helpArticlesSlice = createSlice({
  name: "helpArticles",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHelpArticle.pending, (state, action) => {
        const { modelName, lang } = action.meta.arg;
        state.byKey[helpArticleKey(lang, modelName)] = { status: "loading", article: null };
      })
      .addCase(
        fetchHelpArticle.fulfilled,
        (state, action: PayloadAction<{ key: string; article: HelpArticle | null }>) => {
          const { key, article } = action.payload;
          state.byKey[key] = { status: article ? "ready" : "none", article };
        },
      )
      .addCase(fetchHelpArticle.rejected, (state, action) => {
        // The form must never degrade because help is unavailable — record a
        // terminal "none" so we don't hammer a failing upstream on every render.
        const { modelName, lang } = action.meta.arg;
        state.byKey[helpArticleKey(lang, modelName)] = { status: "none", article: null };
      });
  },
});

export const selectHelpArticleEntry =
  (lang: string, modelName: string) =>
  (state: RootState): HelpArticleEntry | undefined =>
    state.helpArticles.byKey[helpArticleKey(lang, modelName)];

export default helpArticlesSlice.reducer;
